import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, Menu, Upload, ReceiptIcon, LogOut, Settings, BarChart3, User } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { UploadArea } from "@/components/UploadArea";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/currency";
import { Footer } from "@/components/Footer";

interface StoreWithCount {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  country: string | null;
  receipt_count: number;
  total_spent: number;
  currency: string;
}

interface ReceiptWithStore {
  store_id: string;
  total: number | null;
  currency: string | null;
  stores: {
    id: string;
    name: string;
    address: string | null;
    city: string | null;
    country: string | null;
  } | null;
}

const Stores = () => {
  const navigate = useNavigate();
  const [showUpload, setShowUpload] = useState(false);
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: t('toast.success'),
      description: t('profile.accountInformation'),
    });
    navigate("/auth");
  };

  const { data: stores, isLoading } = useQuery({
    queryKey: ['stores-with-receipts'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: profile } = await supabase
        .from('profiles')
        .select('default_currency')
        .eq('id', user.id)
        .single();

      const defaultCurrency = profile?.default_currency || 'USD';

      // Get all receipts for the user with store info
      const { data: receipts, error } = await supabase
        .from('receipts')
        .select('store_id, total, currency, stores(id, name, address, city, country)')
        .eq('user_id', user.id)
        .not('store_id', 'is', null);

      if (error) throw error;

      // Group by store and count
      const storeMap = new Map<string, StoreWithCount>();

      receipts?.forEach((receipt: ReceiptWithStore) => {
        if (receipt.stores) {
          const storeId = receipt.store_id;
          const receiptTotal = Number(receipt.total) || 0;
          const receiptCurrency = receipt.currency || defaultCurrency;
          if (storeMap.has(storeId)) {
            const existingStore = storeMap.get(storeId)!;
            existingStore.receipt_count += 1;
            existingStore.total_spent += receiptTotal;
            if (receipt.currency && existingStore.currency === defaultCurrency) {
              existingStore.currency = receiptCurrency;
            }
          } else {
            storeMap.set(storeId, {
              id: receipt.stores.id,
              name: receipt.stores.name,
              address: receipt.stores.address,
              city: receipt.stores.city,
              country: receipt.stores.country,
              receipt_count: 1,
              total_spent: receiptTotal,
              currency: receiptCurrency,
            });
          }
        }
      });

      return Array.from(storeMap.values()).sort((a, b) => b.receipt_count - a.receipt_count);
    },
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b shadow-custom-sm">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 md:gap-4 min-w-0">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="shrink-0">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72">
                  <SheetHeader>
                    <SheetTitle className="text-left">{t('common.menu')}</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 flex flex-col gap-2">
                    <div
                      className="mb-4 rounded-lg bg-accent p-4 cursor-pointer hover:bg-accent/80 transition-colors"
                      onClick={() => navigate('/profile')}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
                          <User className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user?.email || "User"}</p>
                          <p className="text-xs text-muted-foreground">{t('navigation.profile')}</p>
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => navigate('/?tab=receipts')}
                    >
                      <ReceiptIcon className="mr-3 h-4 w-4" />
                      {t('navigation.receipts')}
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => navigate('/stores')}
                    >
                      <Building2 className="mr-3 h-4 w-4" />
                      {t('navigation.stores')}
                    </Button>

                    <Button variant="ghost" className="justify-start" disabled>
                      <BarChart3 className="mr-3 h-4 w-4" />
                      {t('navigation.analytics')}
                      <span className="ml-auto text-xs text-muted-foreground">{t('common.comingSoon')}</span>
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => navigate('/profile')}
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      {t('navigation.settings')}
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start text-destructive hover:text-destructive"
                      onClick={handleSignOut}
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      {t('common.signOut')}
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              
              <div className="min-w-0">
                <h1
                  className="text-xl md:text-3xl font-bold bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity truncate"
                  onClick={() => navigate('/')}
                >
                  {t('common.appName')}
                </h1>
                <p className="text-xs md:text-sm text-muted-foreground mt-0.5 md:mt-1 hidden sm:block">
                  {t('common.appDescription')}
                </p>
              </div>
            </div>
            <Button
              size="default"
              className="transition-all duration-300 shrink-0 md:size-lg"
              onClick={() => setShowUpload(true)}
            >
              <Upload className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">{t('buttons.uploadReceipt')}</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-6">
        <h2 className="text-2xl font-bold">{t('dashboard.allStores')}</h2>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
      ) : stores && stores.length > 0 ? (
        <div className="space-y-3">
          {stores.map((store) => (
            <Card
              key={store.id}
              data-testid="store-card"
              className="cursor-pointer hover:bg-accent transition-colors"
              onClick={() => navigate(`/stores/${store.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex gap-3">
                    <Building2 className="h-8 w-8 text-muted-foreground" />
                    <div>
                      <p className="font-semibold text-lg" data-testid="store-name">{store.name}</p>
                      {store.city && (
                        <p className="text-sm text-muted-foreground">
                          {store.city}{store.country && `, ${store.country}`}
                        </p>
                      )}
                      {store.address && (
                        <p className="text-xs text-muted-foreground mt-1">{store.address}</p>
                      )}
                      <p className="text-sm text-muted-foreground mt-2">
                        {t('stores.totalSpendLabel')}: {formatCurrency(store.total_spent, store.currency)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-sm md:text-base shrink-0">
                    {t('stores.receiptCount', { count: store.receipt_count })}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('stores.noStores')}</p>
          </CardContent>
        </Card>
      )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <UploadArea
            onUpload={(file) => {
              console.log('File uploaded:', file);
              setShowUpload(false);
              window.location.reload();
            }}
          />
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
};

export default Stores;